<template>
	<DsModal
		@close-modal="setPopupMode('none')"
		:close-label="closeModalLabel ?? 'Close'"
		:is-open="show === 'country'"
		overflow="container"
		size="md"
	>
		<CountrySwitcher
			:country-label-text="confirmCountryViewModel.CountryLabelText"
			:country-list="confirmCountryViewModel.CountryList"
			:description="confirmCountryViewModel.Description"
			:headline="confirmCountryViewModel.Headline"
			:select-country-label-text="confirmCountryViewModel.SelectCountryLabelText ?? confirmCountryViewModel.CountryLabelText"
			@update:selected-country="setSelectedCountry"
		/>

		<template #actions>
			<DsButton
				variant="primary"
				class="c-culture__submit"
				@click="submitLanguage"
			>
				{{ confirmCountryViewModel.ContinueButtonText ?? 'Continue' }}
			</DsButton>
		</template>
	</DsModal>
</template>

<script lang="ts" setup>
import { computed, onMounted, ref } from 'vue';
import { DsButton, DsModal } from '@coloplast/design-system';
import { useCultureNavigation } from 'shared/composables/useCultureNavigation';
import CountrySwitcher from './CountrySwitcher.vue';
import { type Country } from './CountrySwitcher.types';

type RawCountry = {
	Name?: string;
	name?: string;
	Url?: string;
	url?: string;
	Language?: string;
	language?: string;
	IsSelected?: boolean;
	isSelected?: boolean;
	Region?: string;
	region?: string;
};

type RawCountrySwitcherViewModel = {
	BaseViewModel?: RawCountrySwitcherViewModel;
	baseViewModel?: RawCountrySwitcherViewModel;
	CountryList?: RawCountry[];
	countryList?: RawCountry[];
	Description?: string;
	description?: string;
	ContinueButtonText?: string;
	continueButtonText?: string;
	CountryLabelText?: string;
	countryLabelText?: string;
	SelectCountryLabelText?: string;
	selectCountryLabelText?: string;
	Headline?: string;
	headline?: string;
	IsVisible?: boolean;
	isVisible?: boolean;
};

type ConfirmCountryViewModel = {
	CountryList: Country[];
	Description?: string;
	ContinueButtonText?: string;
	CountryLabelText?: string;
	SelectCountryLabelText?: string;
	Headline?: string;
	IsVisible?: boolean;
};

const props = defineProps<{
	closeModalLabel?: string;
	vmc: RawCountrySwitcherViewModel;
}>();

const show = ref<string>('none');
const selectedCountry = ref<Country | null>(null);

const { setCultureCookie } = useCultureNavigation();

const getBaseViewModel = (
	viewModel: RawCountrySwitcherViewModel
): RawCountrySwitcherViewModel => {
	const firstLevel = viewModel.BaseViewModel ?? viewModel.baseViewModel ?? viewModel;

	return firstLevel.BaseViewModel ?? firstLevel.baseViewModel ?? firstLevel;
};

const normalizeCountry = (country: RawCountry): Country => {
	return {
		Name: country.Name ?? country.name ?? '',
		Url: country.Url ?? country.url ?? '',
		Language: country.Language ?? country.language ?? '',
		IsSelected: country.IsSelected ?? country.isSelected ?? false,
		Region: country.Region ?? country.region ?? '',
	};
};

const confirmCountryViewModel = computed<ConfirmCountryViewModel>(() => {
	const baseViewModel = getBaseViewModel(props.vmc);

	return {
		CountryList: (baseViewModel.CountryList ?? baseViewModel.countryList ?? [])
			.map(normalizeCountry)
			.filter((country) => country.Name.length && country.Url.length),
		Description: baseViewModel.Description ?? baseViewModel.description,
		ContinueButtonText:
			baseViewModel.ContinueButtonText ?? baseViewModel.continueButtonText,
		CountryLabelText:
			baseViewModel.CountryLabelText ?? baseViewModel.countryLabelText,
		SelectCountryLabelText:
			baseViewModel.SelectCountryLabelText ??
			baseViewModel.selectCountryLabelText,
		Headline: baseViewModel.Headline ?? baseViewModel.headline,
		IsVisible: baseViewModel.IsVisible ?? baseViewModel.isVisible,
	};
});

const setPopupMode = (name: string) => {
	show.value = name;
};

const setSelectedCountry = (country: Country) => {
	selectedCountry.value = country;
};

const submitLanguage = () => {
	if (!selectedCountry.value) {
		return;
	}

	setCultureCookie(selectedCountry.value.Name);
	setPopupMode('none');
	globalThis.location.assign(selectedCountry.value.Url);
};

onMounted(() => {
	const countryList = confirmCountryViewModel.value.CountryList;
	const selected = countryList.find((country) => country.IsSelected);

	if (!selected) {
		return;
	}

	setSelectedCountry(selected);
	setPopupMode('country');
});
</script>
